import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Loader2, CheckCircle2, XCircle, ClipboardCheck, CalendarX2, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

export default function LectioTest({ courseId, index, argomento, onExit, onPassed }) {
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(false);
  const [error, setError] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [soglia, setSoglia] = useState(9);
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const generate = () => {
    setLoading(true);
    setResult(null);
    setAnswers({});
    setBlocked(false);
    setError(false);
    api.post(`/courses/${courseId}/lesson/${index}/test/generate`)
      .then((res) => {
        const qs = res.data?.domande || [];
        if (!qs.length) { setError(true); return; }
        setQuestions(qs);
        setSoglia(res.data.soglia);
        setAttemptsLeft(res.data.attempts_left);
      })
      .catch((e) => {
        if (e.response?.status === 429) setBlocked(true);
        else setError(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, [courseId, index]);

  const setAnswer = (id, val) => setAnswers((a) => ({ ...a, [id]: val }));

  const submit = async () => {
    if (questions.filter((q) => answers[q.id] === undefined).length) {
      toast.error("Rispondi a tutte le domande.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { answers: questions.map((q) => ({ question_id: q.id, answer: String(answers[q.id]) })) };
      const { data } = await api.post(`/courses/${courseId}/lesson/${index}/test/submit`, payload);
      setResult(data);
      if (data.passed) { toast.success("Lectio compresa! Test superato."); onPassed && onPassed(); }
      else toast.error("Test non superato. Devi ripeterlo.");
    } catch (e) {
      if (e.response?.status === 429) { setBlocked(true); setResult(null); }
      else toast.error("Errore durante l'invio del test");
    } finally {
      setSubmitting(false);
    }
  };

  const header = (
    <button onClick={onExit} data-testid="lectiotest-exit-btn"
      className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6 transition-colors">
      <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Torna alla lectio
    </button>
  );

  if (loading)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="py-32 flex flex-col items-center gap-4 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" strokeWidth={1.5} />
          <p className="text-muted-foreground">Preparazione della Prova di comprensione...</p>
        </div>
      </div>
    );

  if (blocked)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-xl mx-auto px-5 sm:px-8 py-10">
          {header}
          <div className="bg-card border border-border rounded-md p-8 text-center" data-testid="lectiotest-blocked">
            <CalendarX2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" strokeWidth={1.5} />
            <h1 className="font-serif text-2xl font-bold mb-2">Tentativi esauriti per oggi</h1>
            <p className="text-muted-foreground mb-6">
              Hai già utilizzato i 2 tentativi giornalieri per questa lectio. Ripassa il contenuto e riprova domani.
            </p>
            <Button onClick={onExit} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="lectiotest-back-btn">
              Torna alla lectio
            </Button>
          </div>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-xl mx-auto px-5 sm:px-8 py-10">
          {header}
          <div className="bg-card border border-border rounded-md p-8 text-center" data-testid="lectiotest-error">
            <XCircle className="h-12 w-12 mx-auto text-destructive/70 mb-4" strokeWidth={1.5} />
            <h1 className="font-serif text-2xl font-bold mb-2">Prova non disponibile</h1>
            <p className="text-muted-foreground mb-6">
              Non è stato possibile preparare la Prova in questo momento. Riprova tra poco.
            </p>
            <Button onClick={generate} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="lectiotest-error-retry">
              <RotateCcw className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Riprova
            </Button>
          </div>
        </div>
      </div>
    );

  if (result)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-xl mx-auto px-5 sm:px-8 py-10">
          {header}
          <div className="bg-card border border-border rounded-md p-8 text-center">
            {result.passed
              ? <CheckCircle2 className="h-14 w-14 mx-auto text-primary mb-4" strokeWidth={1.5} />
              : <XCircle className="h-14 w-14 mx-auto text-destructive mb-4" strokeWidth={1.5} />}
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-1">Prova di comprensione</p>
            <h1 className="font-serif text-5xl font-bold mb-2" data-testid="lectiotest-score">
              {result.correct}/{result.total}
            </h1>
            <Badge className={`rounded-full mb-6 ${result.passed ? "bg-primary text-primary-foreground" : "bg-destructive text-destructive-foreground"}`}>
              {result.passed ? "Superato" : "Non superato"}
            </Badge>
            <p className="text-muted-foreground mb-6">
              {result.passed
                ? "Hai dimostrato di aver compreso la lectio. Puoi proseguire con la prossima."
                : `Per superare servono almeno ${result.soglia} risposte corrette. Devi rifare la Prova.`}
            </p>

            {result.passed ? (
              <Button onClick={onExit} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="lectiotest-continue-btn">
                Continua
              </Button>
            ) : result.can_retry_today ? (
              <div className="flex flex-col items-center gap-2">
                <Button onClick={generate} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="lectiotest-retry-btn">
                  <RotateCcw className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Rifai la Prova
                </Button>
                <p className="text-xs text-muted-foreground">Tentativi rimasti oggi: {result.attempts_left}</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <CalendarX2 className="h-4 w-4" strokeWidth={1.5} /> Hai esaurito i tentativi di oggi. Riprova domani.
                </p>
                <Button onClick={onExit} variant="outline" className="rounded-sm mt-2" data-testid="lectiotest-back-btn">
                  Torna alla lectio
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-8">
        {header}
        <div className="flex items-center gap-2 mb-2">
          <ClipboardCheck className="h-5 w-5 text-primary" strokeWidth={1.5} />
          <p className="text-sm uppercase tracking-widest text-muted-foreground">Prova di comprensione</p>
        </div>
        <h1 className="font-serif text-3xl font-bold mb-1">{argomento}</h1>
        <p className="text-muted-foreground text-sm mb-6">
          {questions.length} domande · servono {soglia} risposte corrette per superare
          {attemptsLeft != null && ` · tentativi rimasti oggi: ${attemptsLeft}`}
        </p>

        <div className="space-y-4">
          {questions.map((q, i) => (
            <div key={q.id} className="bg-card border border-border rounded-md p-5" data-testid={`test-question-${i}`}>
              <div className="flex items-start gap-3 mb-4">
                <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center">{i + 1}</span>
                <p className="font-medium leading-snug pt-0.5">{q.testo}</p>
              </div>
              <RadioGroup value={answers[q.id] ?? ""} onValueChange={(v) => setAnswer(q.id, v)} className="space-y-2 ml-10">
                {q.opzioni.map((opt, oi) => (
                  <label key={`${q.id}-${oi}`} htmlFor={`${q.id}-${oi}`}
                    className="flex items-center gap-3 p-3 rounded-sm border border-border cursor-pointer hover:bg-secondary/50 transition-colors">
                    <RadioGroupItem value={String(oi)} id={`${q.id}-${oi}`} data-testid={`test-option-${i}-${oi}`} />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          ))}
        </div>

        <Button onClick={submit} disabled={submitting}
          className="w-full mt-8 rounded-sm bg-primary hover:bg-primary/90 h-12" data-testid="test-submit-btn">
          {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {submitting ? "Correzione in corso..." : "Invia la Prova"}
        </Button>
      </div>
    </div>
  );
}
